import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { FilterBar, FilterEnd, FilterSelect, Segmented } from '../components/FilterBar';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import EmptyState from '../components/ui/EmptyState';
import { Field } from '../components/ui/Field';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { buttonClass } from '../components/ui/Button';
import BoxEmptyDialog from './BoxEmptyDialog';
import PoolLines, { RequestBand, type BandRequest, type LinesMeta, TOUCH } from './PoolLines';
import PoolPicked, { type PickedMeta } from './PoolPicked';
import { useWide } from './useWide';
import {
  useWorkOrdersApi,
  workOrderErrorMessage,
  type AssemblyPickList,
  type BoxRef,
  type PickResult,
  type PoolLine,
} from './workOrdersApi';

const REFRESH_MS = 30_000;

type Tab = 'move' | 'picked';

const HELP = (
  <>
    <p>
      One list for the whole floor: everything the approved runs still need in Assembly, summed per part, against
      what Assembly already holds. A run counts from the moment it is approved until it closes. A run on hold does
      not count, and neither does a suggestion.
    </p>
    <p>
      A run needs parts for the drones it has not built yet. A part already scanned onto a drone in progress is not
      asked for again. A drone flagged off a run needs a full set again.
    </p>
    <p className="font-medium text-on-surface">Picking</p>
    <p>
      Scan a box and the whole box moves to Assembly, whatever else is in it. Assembly is one pool: no box belongs to
      a run, and the runs draw from it oldest first. Each run’s page says whether the pool covers it.
    </p>
    <p>
      When a drone at the station comes up short, the station asks for a box. The request shows at the top of this
      page until someone picks a box holding that part, which fills it.
    </p>
    <p className="font-medium text-on-surface">Already picked</p>
    <p>
      Every box in Assembly, with the section it came from. Boxes go back with the handheld scanner: scan the box,
      then scan a section. Mark empty writes a box down to zero when the shelf count said it held parts and it did not.
    </p>
  </>
);

function pickedSentence(res: PickResult, label: string): string {
  const to = res.assembly?.name || res.moved[0]?.to || 'Assembly';
  if (res.moved.length === 1) {
    const m = res.moved[0];
    return `Picked ${m.barcode || label}, ${m.quantity} ${m.display_name || m.sku || 'items'} into ${to}.`;
  }
  const total = res.moved.reduce((sum, m) => sum + (m.quantity || 0), 0);
  return `Picked ${label}, ${total} parts across ${res.moved.length} items into ${to}.`;
}

const toggled = <T,>(set: ReadonlySet<T>, v: T): Set<T> => {
  const next = new Set(set);
  if (next.has(v)) next.delete(v);
  else next.add(v);
  return next;
};

const AssemblyPickPage: React.FC = () => {
  const woApi = useWorkOrdersApi();
  const toast = useToast();
  const wide = useWide();
  const user = useUserStore((s) => s.user);
  const isAdmin = !!(user?.isadmin || user?.can_admin);
  const canPick = isAdmin || !!user?.can_manufacturing;

  const [data, setData] = useState<AssemblyPickList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scan, setScan] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyWord, setBusyWord] = useState('Picking…');
  const [tab, setTab] = useState<Tab>('move');
  const [cancelling, setCancelling] = useState<string | null>(null);
  // A line with a request opens its boxes by default; a press flips that.
  const [flipped, setFlipped] = useState<ReadonlySet<string>>(() => new Set());
  const [expandedHolds, setExpandedHolds] = useState<ReadonlySet<string>>(() => new Set());
  const [justCovered, setJustCovered] = useState<ReadonlySet<string>>(() => new Set());
  const scanRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const dataRef = useRef<AssemblyPickList | null>(null);
  dataRef.current = data;

  const load = useCallback(async () => {
    try {
      const next = await woApi.assemblyPickList();
      const prev = dataRef.current;
      if (prev) {
        const before = new Map(prev.lines.map((l) => [l.key, l.to_move]));
        setJustCovered(new Set(next.lines.filter((l) => l.to_move === 0 && (before.get(l.key) ?? 0) > 0).map((l) => l.key)));
      }
      setData(next);
      setLoadError(null);
    } catch (err) {
      const message = workOrderErrorMessage(err, 'The pick list did not load.');
      setLoadError(message);
      if (dataRef.current) toast.error(`${message} What is shown may be out of date.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // Stations raise requests while this page sits open on the kitting cart.
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !busyRef.current) load();
    }, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const run = async (word: string, act: () => Promise<void>) => {
    if (busy) return;
    setBusyWord(word);
    setBusy(true);
    try {
      await act();
    } finally {
      setBusy(false);
    }
  };

  const pick = (box: BoxRef, label: string) =>
    run('Picking…', async () => {
      try {
        const res = await woApi.assemblyPick(box);
        if (!res?.moved?.length) toast.info(`${label} had nothing left to move.`);
        else toast.success(pickedSentence(res, label));
        await load();
      } catch (err) {
        toast.error(workOrderErrorMessage(err, 'The box did not move. Scan it again.'));
      }
    });

  const submitScan = async () => {
    const code = scan.trim();
    if (!code) return;
    // Cleared before the await so the next wedge-scanner burst starts on an empty field.
    setScan('');
    if (busy) {
      toast.warning(`Wait for the last box to finish, then scan ${code} again.`);
      return;
    }
    await pick({ barcode: code }, code);
    scanRef.current?.focus();
  };

  const cancelRequest = async (r: BandRequest) => {
    if (cancelling) return;
    setCancelling(r.id);
    try {
      await woApi.cancelPickRequest(r.work_order_id, r.id);
      toast.success(`Cancelled the request for ${r.target_name} on ${r.number}.`);
      await load();
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The request was not cancelled. Try again.'));
    } finally {
      setCancelling(null);
    }
  };

  const lines = useMemo(() => data?.lines ?? [], [data]);
  const picked = useMemo(() => data?.picked ?? [], [data]);
  const requests = useMemo<BandRequest[]>(
    () =>
      lines
        .flatMap((l) => l.requests.map((r) => ({ ...r, target_name: l.target_name })))
        .sort((a, b) => (a.requested_at || '').localeCompare(b.requested_at || '')),
    [lines],
  );
  // Requested lines first, the rest in the server's order.
  const ordered = useMemo(
    () => [...lines.filter((l) => l.requests.length), ...lines.filter((l) => !l.requests.length)],
    [lines],
  );
  const toMove = lines.filter((l) => l.to_move > 0).length;

  const [area, setArea] = useState('');
  const [section, setSection] = useState('');
  // Options come from the boxes in the response, so a rack nobody stocks never clutters the list.
  const areaOptions = useMemo(
    () => [...new Set(lines.flatMap((l) => l.boxes.map((b) => b.area_name)).filter((a): a is string => !!a))].sort(),
    [lines],
  );
  // A choice that vanished from a refresh must not leave the list silently empty.
  const areaActive = area && areaOptions.includes(area) ? area : '';
  const sectionOptions = useMemo(
    () =>
      [
        ...new Set(
          lines
            .flatMap((l) => l.boxes)
            .filter((b) => !areaActive || b.area_name === areaActive)
            .map((b) => b.section_code)
            .filter((c): c is string => !!c),
        ),
      ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [lines, areaActive],
  );
  const sectionActive = section && sectionOptions.includes(section) ? section : '';
  // Drop the stored choice too, or it would silently return when the option reappears.
  useEffect(() => {
    if (area && !areaActive) { setArea(''); setSection(''); }
    else if (section && !sectionActive) setSection('');
  }, [area, areaActive, section, sectionActive]);
  const filtering = !!(areaActive || sectionActive);
  const shown = useMemo(
    () =>
      !filtering
        ? ordered
        : ordered.filter((l) =>
            l.boxes.some(
              (b) => (!areaActive || b.area_name === areaActive) && (!sectionActive || b.section_code === sectionActive),
            ),
          ),
    [ordered, filtering, areaActive, sectionActive],
  );

  const linesMeta = useMemo<LinesMeta>(
    () => ({
      justCovered,
      isOpen: (l: PoolLine) => (l.requests.length > 0) !== flipped.has(l.key),
      onToggle: (l: PoolLine) => setFlipped((prev) => toggled(prev, l.key)),
      canPick,
      busy,
      onPickBox: pick,
    }),
    // `pick` closes over setters, the api module and `busy`, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [justCovered, flipped, canPick, busy],
  );

  const pickedMeta = useMemo<PickedMeta>(
    () => ({
      expandedHolds,
      onToggleHolds: (barcode: string) => setExpandedHolds((prev) => toggled(prev, barcode)),
      canPick,
      busy,
      onEmptied: load,
    }),
    [expandedHolds, canPick, busy, load],
  );

  const header = (
    <PageHeader
      title="Pick list"
      helpTitle="About the pick list"
      lede="What needs to move to Assembly right now, for every approved run."
      help={HELP}
      actions={
        <SecondaryAction variant="text" to="/work-orders">
          Work orders
        </SecondaryAction>
      }
    />
  );

  if (loadError && !data) {
    return (
      <PageLayout width="wide" kind="workspace" className="space-y-5 pt-4" header={header}>
        <Alert
          tone="error"
          onRetry={load}
          action={
            <Link to="/work-orders" className={buttonClass('quiet', 'md')}>
              All work orders
            </Link>
          }
        >
          {loadError}
        </Alert>
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout width="wide" kind="workspace" className="space-y-5 pt-4" header={header}>
        <LogoSpinner label="Loading the pick list" />
      </PageLayout>
    );
  }

  if (!data.assembly) {
    return (
      <PageLayout width="wide" kind="workspace" className="space-y-5 pt-4" header={header}>
        <EmptyState
          title="No Assembly yet"
          action={
            isAdmin ? (
              <Link to="/admin/locations" className={buttonClass('tonal', 'md')}>
                Open Locations
              </Link>
            ) : undefined
          }
        >
          Picks land in the one location tagged as Assembly, and none is tagged yet.
          {isAdmin ? ' Switch Assembly on for the place the line builds from.' : ' Ask an admin to tag one in Locations.'}
        </EmptyState>
      </PageLayout>
    );
  }

  const moveStatus = (
    <span className="text-xs tabular-nums text-on-surface-variant">
      {filtering
        ? `${shown.length} of ${lines.length} ${lines.length === 1 ? 'part' : 'parts'} have a box here`
        : lines.length === 0
        ? 'Nothing to move'
        : toMove === 0
          ? `${data.assembly.name} holds everything the runs need.`
          : `${toMove} of ${lines.length} ${lines.length === 1 ? 'part' : 'parts'} still to move`}
    </span>
  );
  const moveEmpty = filtering ? (
    <EmptyState
      variant="area"
      action={
        <button
          type="button"
          onClick={() => {
            setArea('');
            setSection('');
          }}
          className={`${buttonClass('tonal', 'sm')} ${wide ? '' : TOUCH}`}
        >
          Clear filters
        </button>
      }
    >
      No part on the list has a box there.
    </EmptyState>
  ) : (
    <EmptyState
      variant="area"
      title="Nothing needs to move to Assembly."
      action={
        <Link to="/work-orders" className={`${buttonClass('quiet', 'sm')} ${wide ? '' : TOUCH}`}>
          See the work orders
        </Link>
      }
    >
      Every approved run is covered by what {data.assembly.name} already holds. When a manager approves the next run,
      its parts show here.
    </EmptyState>
  );
  const pickedStatus = (
    <span className="text-xs tabular-nums text-on-surface-variant">
      {`${picked.length} ${picked.length === 1 ? 'box' : 'boxes'} in ${data.assembly.name}`}
    </span>
  );
  const pickedEmpty = (
    <EmptyState
      variant="area"
      action={
        <button type="button" onClick={() => setTab('move')} className={`${buttonClass('quiet', 'sm')} ${wide ? '' : TOUCH}`}>
          Show what to move
        </button>
      }
    >
      {canPick ? `${data.assembly.name} is empty. Scan a box above, or pick one from the list.` : `${data.assembly.name} is empty.`}
    </EmptyState>
  );

  return (
    <PageLayout width="wide" kind="workspace" className="space-y-5 pt-4" header={header}>
      <Panel label="Picks into Assembly" padding="none" className="overflow-hidden">
        <div className="space-y-3 px-4 pb-3 pt-4">
          <p className="text-xs text-on-surface-variant">Picks land in {data.assembly.name}.</p>
          {canPick && (
            <div className="flex flex-wrap items-center gap-2">
              <Field
                ref={scanRef}
                type="text"
                thumb
                // A wedge scanner types into whatever has focus, so the cart needs no tap first. Not on a
                // phone, where focus would raise the on-screen keyboard over the list.
                autoFocus={wide}
                autoComplete="off"
                spellCheck={false}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitScan();
                  }
                }}
                aria-label="Scan or type a box barcode, Enter picks it into Assembly"
                placeholder="Scan a box, Enter picks it into Assembly"
                aria-busy={busy}
                className="flex-1 basis-64 font-mono"
              />
              <BoxEmptyDialog barcode={scan} triggerClassName={buttonClass('quiet', 'sm')} onDone={load} />
              <span role="status" className="text-xs text-on-surface-variant">
                {busy ? busyWord : ''}
              </span>
            </div>
          )}
          <Segmented
            label="Which list to show"
            className="max-w-full"
            options={[
              { key: 'move', label: 'To move', badge: data.open_requests || undefined },
              { key: 'picked', label: 'Already picked', badge: picked.length },
            ]}
            value={tab}
            onChange={(k) => setTab(k as Tab)}
          />
        </div>
        <Reveal show={tab === 'move' && areaOptions.length > 0}>
          <div className="px-3 pb-3">
            <FilterBar label="Filter the pick list">
              <FilterSelect label="Area" hideLabel value={areaActive} onChange={(v) => { setArea(v); setSection(''); }}>
                <option value="">All areas</option>
                {areaOptions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </FilterSelect>
              <FilterSelect label="Section" hideLabel value={sectionActive} onChange={setSection}>
                <option value="">All sections</option>
                {sectionOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </FilterSelect>
              {filtering && (
                <FilterEnd>
                  <button
                    type="button"
                    onClick={() => {
                      setArea('');
                      setSection('');
                    }}
                    className={buttonClass('quiet', 'sm')}
                  >
                    Clear filters
                  </button>
                </FilterEnd>
              )}
            </FilterBar>
          </div>
        </Reveal>
        <Reveal show={tab === 'move' && requests.length > 0}>
          <RequestBand
            requests={requests}
            canCancel={canPick}
            cancelling={cancelling}
            onCancel={cancelRequest}
            className={wide ? 'px-4 pb-3' : 'px-3 pb-2'}
          />
        </Reveal>
        <FadeIn key={tab}>
          {tab === 'move' ? (
            <PoolLines lines={shown} meta={linesMeta} wide={wide} status={moveStatus} empty={moveEmpty} />
          ) : (
            <PoolPicked picked={picked} meta={pickedMeta} wide={wide} status={pickedStatus} empty={pickedEmpty} />
          )}
        </FadeIn>
      </Panel>
    </PageLayout>
  );
};

export default AssemblyPickPage;
